import { z } from "zod";
import { IdentifiedOrDeletedAuthor } from "../author";
import { ImageSourceSchema } from "../image";
import { IsoDateString } from "../isoDate";
import { UserPostReactionSchema } from "../resources";
import { TipTapDocSchema } from "../tiptapDoc";

/**
 * Cached stats for a video preview. Populated by the scheduled-publish cron
 * from YouTube / Vimeo APIs (or derived directly from an uploaded asset).
 * Raw integers so consumers can format however they want; `fetchedAt` lets
 * callers display "as of …" or suppress stale values.
 */
export const VideoPreviewStatsSchema = z.object({
    viewCount: z.number().nullish(),
    durationSeconds: z.number().nullish(),
    fetchedAt: z.string().nullish(),
});
export type VideoPreviewStats = z.infer<typeof VideoPreviewStatsSchema>;

/**
 * Preview media for a BlogPost card/detail hero. Tagged by `type` so new video
 * providers (Wistia, Loom, etc.) can be added without changing the storage
 * shape — DB persists the flat `(previewKind, previewUrl, previewAssetId)`
 * triple and `BlogPostService` encodes/decodes. Video variants surface cached
 * stats so UI can show view count / duration without hitting the provider.
 */
/**
 * For video variants, the server emits two URLs:
 *   - `url` — what the author entered (the canonical YouTube watch URL, the
 *     canonical Vimeo URL, or the hosted-video CDN URL). UIs use this for
 *     "open in new tab" links.
 *   - `embedSrc` — pre-resolved URL safe to drop into an `<iframe src>` (or
 *     `<video src>` for hosted variants). YouTube `watch?v=` URLs refuse to
 *     be framed, so the server rewrites to `youtube.com/embed/<id>`. Same
 *     idea for Vimeo's `player.vimeo.com/video/<id>`. Null when the URL
 *     can't be parsed — UI should fall back to the thumbnail + external link.
 */
export const BlogPostPreviewSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("none") }),
    z.object({ type: z.literal("image-url"), url: z.string(), alt: z.string().nullish() }),
    z.object({
        type: z.literal("image-asset"),
        url: z.string(),
        alt: z.string().nullish(),
        /** PRTL-1349: sized WebP copies of the asset, once generated. */
        image: ImageSourceSchema.optional(),
    }),
    z.object({
        type: z.literal("video-url"),
        url: z.string(),
        embedSrc: z.string().nullable(),
        stats: VideoPreviewStatsSchema,
    }),
    z.object({
        type: z.literal("video-asset"),
        url: z.string(),
        embedSrc: z.string().nullable(),
        stats: VideoPreviewStatsSchema,
    }),
    z.object({
        type: z.literal("youtube-embed"),
        url: z.string(),
        embedSrc: z.string().nullable(),
        stats: VideoPreviewStatsSchema,
    }),
    z.object({
        type: z.literal("vimeo-embed"),
        url: z.string(),
        embedSrc: z.string().nullable(),
        stats: VideoPreviewStatsSchema,
    }),
    z.object({
        type: z.literal("apple-podcast-embed"),
        url: z.string(),
        embedSrc: z.string().nullable(),
        kind: z.enum(["episode", "show"]).default("episode"),
    }),
]);
export type BlogPostPreview = z.infer<typeof BlogPostPreviewSchema>;

/**
 * Author display payload surfaced on every blog post response (byline, cards, favourites) and,
 * with a bio and post count, on the author page. Built server-side by one resolver
 * (`buildBlogPostAuthor`), so every surface shows the same name (PRTL-1508).
 *
 * A blog author is a ContentAuthor, which may be backed by a User. That User is carried as the
 * author chip union (`user`), never as a flat id: a soft-deleted user is the DeletedAuthor
 * tombstone, and clients link to a profile only when `user.type === "identified"`.
 */
export const BlogPostAuthorSchema = z.object({
    /** ContentAuthor.id, for the author page. Null when the post has no (live) author. */
    id: z.number().nullable(),
    /**
     * Byline text: the ContentAuthor's display name, else the backing user's chip name
     * ("Deleted User" for a deleted user), else "Compass" for a house post.
     */
    name: z.string(),
    avatarUrl: z.string().nullable(),
    /** The User behind the ContentAuthor; null for a guest byline or a post with no author. */
    user: IdentifiedOrDeletedAuthor.nullable(),
});
export type BlogPostAuthor = z.infer<typeof BlogPostAuthorSchema>;

export const BlogPostAuthorProfileSchema = BlogPostAuthorSchema.extend({
    id: z.number(),
    bio: z.string().nullable(),
    postCount: z.number(),
});
export type BlogPostAuthorProfile = z.infer<typeof BlogPostAuthorProfileSchema>;

export const ResourcesPostPreviewSchema = z.object({
    id: z.number(),
    slug: z.string(),
    title: z.string(),
    publishedAt: IsoDateString,
    author: BlogPostAuthorSchema,
    /** Markdown preview text rendered verbatim on cards (no auto-truncation). */
    excerpt: z.string(),
    preview: BlogPostPreviewSchema,
    categories: z.array(z.string()),
    /** How the thumbnail is fitted into the card slot. */
    previewFit: z.enum(["cover", "contain", "manual"]),
    /** Focal point X (0–100, %) used only when previewFit = "manual". */
    previewPositionX: z.number().finite().min(0).max(100).nullable(),
    /** Focal point Y (0–100, %) used only when previewFit = "manual". */
    previewPositionY: z.number().finite().min(0).max(100).nullable(),
});

export const schema = {
    categories: {
        input: z.void(),
        output: z.array(
            z.union([
                z.object({
                    name: z.string(),
                    slug: z.string(),
                }),
                z.object({
                    name: z.literal("Live"),
                    isLive: z.literal(true),
                }),
            ]),
        ),
    },
    getPostReactions: {
        input: z.object({
            postId: z.number(),
        }),
        output: UserPostReactionSchema,
    },
    postReactions: {
        input: z.object({
            postId: z.number(),
            reaction: z.enum(["LIKE", "UNLIKE"]),
            id: z.number().nullish(),
        }),
        output: z.object({
            success: z.boolean(),
        }),
    },
    refreshPreviewStats: {
        input: z.object({ postId: z.number() }),
        output: VideoPreviewStatsSchema.extend({
            viewCount: z.number().nullable(),
            durationSeconds: z.number().nullable(),
            fetchedAt: z.string().nullable(),
        }),
    },
    sharePost: {
        input: z.object({
            email: z.email(),
            recipientName: z.string(),
            slug: z.string(),
        }),
        output: z.string(),
    },
    getCategory: {
        input: z.object({
            slug: z.string(),
        }),
        output: z.union([
            z.object({
                id: z.number(),
                name: z.string(),
                slug: z.string(),
                parent: z.number(),
                description: z.string(),
            }),
            z.object({
                error: z.enum(["NOT_FOUND"]),
            }),
        ]),
    },
    latestPosts: {
        input: z
            .object({
                category: z.string().optional(),
                authorId: z.number().optional(),
                search: z.string().optional(),
                limit: z.number().optional().default(15),
                page: z.number().optional().default(1),
            })
            .default({
                limit: 15,
                page: 1,
            }),
        output: z.union([
            z.object({
                posts: z.array(ResourcesPostPreviewSchema),
                totalCount: z.number(),
            }),
            z.object({
                error: z.literal("NOT_FOUND"),
            }),
        ]),
    },
    getAuthor: {
        input: z.object({ id: z.number() }),
        output: z.union([BlogPostAuthorProfileSchema, z.object({ error: z.literal("NOT_FOUND") })]),
    },
    popularPosts: {
        input: z.void(),
        output: z.union([
            z.object({
                posts: z.array(ResourcesPostPreviewSchema),
                totalCount: z.number(),
            }),
            z.object({
                error: z.literal("NOT_FOUND"),
            }),
        ]),
    },
    getPost: {
        input: z.object({
            slug: z.string(),
        }),
        output: z.union([
            z.object({
                id: z.number(),
                slug: z.string(),
                title: z.string(),
                publishedAt: IsoDateString,
                /** Post body as a TipTap doc. */
                contentJson: TipTapDocSchema,
                /** Preview text as a TipTap doc; null if no excerpt was set. */
                excerptJson: TipTapDocSchema.nullable(),
                author: BlogPostAuthorSchema,
                preview: BlogPostPreviewSchema,
                allCategories: z.array(z.string()),
            }),
            z.object({
                error: z.enum(["NOT_FOUND"]),
            }),
        ]),
    },
};
